import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Container, Button, TypoGraph } from 'components'
import { userLogin } from 'api'
import { CheckBoxField, TextField } from 'components/controllers'
import { setupLoginCred, isCarrierLogin, isClaimsOnly } from 'tools/auth'
import { ObjectStrings, CompanyProfileState } from 'interfaces'
import strings from 'strings'
import colors from 'theme/colors'
import { reportException } from 'tools/logs'
import { initializePendoAnonymous } from 'tools/pendo'

interface ILoginProps {
  styles: ObjectStrings
  handleStep: (val: number) => void
}

const Login: React.FunctionComponent<ILoginProps> = (props) => {
  const { styles, handleStep } = props
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkedRemember, setCheckedRemember] = useState(false)
  const navigate = useNavigate()
  const companyProfile = useSelector((state: any) => state?.whiteLabel?.companyProfile as CompanyProfileState | null)
  const ssoLoginUrl = companyProfile?.ssoLoginUrl

  const onLoginPressed = async (userName: string, password: string) => {
    setLoading(true)
    userLogin({ UserName: userName, Password: password })
      .then((res: any) => {
        const data = res?.data
        setupLoginCred(data)
        initializePendoAnonymous()
        // CLAIMS_ONLY has no Dashboard entry, so sending it to /dashboard lands the user on the
        // No Access view as their first screen after login. The SSO path and the index route
        // both already branch on isClaimsOnly(); this one was missed.
        navigate(
          isCarrierLogin()
            ? strings.ROUTES.FLEET_REPORTING
            : isClaimsOnly()
            ? strings.ROUTES.EVENTS_FEED.replace('/*', '')
            : '/dashboard'
        )
      })
      .catch((error: any) => {
        reportException(error)
      })
      .finally(() => setLoading(false))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onLoginPressed(userName, password)
    }
  }

  return (
    <Container className={styles.formContainer}>
      <TextField
        label={strings.USER_NAME}
        type="text"
        id={'usernameLogin'}
        onChange={(e: React.ChangeEvent<any>) => setUserName(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <TextField
        label={strings.PASSWORD}
        id={'passwordLogin'}
        onChange={(e: React.ChangeEvent<any>) => setPassword(e.target.value)}
        onKeyDown={handleKeyDown}
        showPasswordIcon
      />
      <Container className={styles.contentLinkContainer}>
        <CheckBoxField
          id={'rememberMeCheckLogin'}
          label="Remember me"
          checked={checkedRemember}
          labelVariant="subtitle1"
          onChange={() => setCheckedRemember(!checkedRemember)}
        />
        <TypoGraph
          id={'forgotPasswordTextLinkDiv'}
          variant="body1"
          content={strings.FORGOT_PASSWORD}
          onClick={() => handleStep(1)}
          color={colors.primary}
          align="right"
          link
        />
      </Container>
      <Container className={styles.buttonContainer}>
        <Button
          id={'loginButton'}
          loading={loading}
          disabled={password === '' || userName === ''}
          variant="contained"
          onClick={() => onLoginPressed(userName, password)}
          label={strings.LOGIN}
        />
      </Container>
      {ssoLoginUrl && (
        <Container className={styles.buttonContainer} sx={{ mt: '12px', mb: '12px' }}>
          <Button
            id={ssoLoginUrl.includes('/sso/profile/') ? 'ssoNonStandardLoginButton' : 'ssoStandardLoginButton'}
            variant="outlined"
            onClick={() => { window.location.href = `${process.env.REACT_APP_CARRIER_API_URL}${ssoLoginUrl}` }}
            label="Login with SSO"
          />
        </Container>
      )}
    </Container>
  )
}

export default Login
